import { useState, type ReactElement } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchTracingHealth,
  fetchTraceOverrides,
  postTraceOverride,
} from '../../api/admin.js';
import { formatNumber, formatPercent, formatDateTime } from '../../lib/format.js';

export function SystemPage(): ReactElement {
  const health = useQuery({
    queryKey: ['admin', 'system', 'tracing'],
    queryFn: fetchTracingHealth,
    refetchInterval: 15_000,
  });
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">System · Tracing</h1>
          <p className="page-sub">Salud del propio pipeline de observabilidad · refresh 15s</p>
        </div>
      </div>
      {health.isPending ? (
        <div className="loading">Cargando…</div>
      ) : health.isError ? (
        <div className="loading">Error: {(health.error).message}</div>
      ) : (
        <>
          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Volumen (spans/min · última hora)</h3>
              <small>{formatNumber(health.data.spansPerMinute.reduce((a, p) => a + p.n, 0))} spans totales</small>
            </header>
            <SparkChart points={health.data.spansPerMinute.map((p) => p.n)} />
          </section>
          <div className="grid-2">
            <section className="card">
              <header className="card-head">
                <h3 className="card-title">Sample reason (24h)</h3>
              </header>
              <table className="data-table">
                <thead><tr><th>Reason</th><th>Count</th></tr></thead>
                <tbody>
                  {health.data.sampleReasonBreakdown.map((r) => (
                    <tr key={r.reason}>
                      <td><code>{r.reason}</code></td>
                      <td>{formatNumber(r.n)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="card">
              <header className="card-head">
                <h3 className="card-title">Por kind (24h)</h3>
              </header>
              <table className="data-table">
                <thead><tr><th>Kind</th><th>Total</th><th>Errors</th><th>Rate</th></tr></thead>
                <tbody>
                  {health.data.kindBreakdown.map((r) => (
                    <tr key={r.kind}>
                      <td><span className={`kind-pill kind-${r.kind}`}>{r.kind}</span></td>
                      <td>{formatNumber(r.total)}</td>
                      <td>{formatNumber(r.errors)}</td>
                      <td className={r.errorRate > 0.05 ? 'bad' : ''}>{formatPercent(r.errorRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
          <TraceOverridesSection activeCount={health.data.activeOverrides} />
        </>
      )}
    </>
  );
}

function SparkChart({ points }: { points: number[] }): ReactElement {
  if (points.length === 0) return <div className="empty">Sin datos</div>;
  const max = Math.max(...points, 1);
  const w = 100;
  const h = 60;
  const step = w / Math.max(points.length - 1, 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(2)} ${(h - (p / max) * h).toFixed(2)}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="spark" style={{ width: '100%', height: 60 }}>
      <path d={path} fill="none" stroke="currentColor" strokeWidth={1.4} />
    </svg>
  );
}

function TraceOverridesSection({ activeCount }: { activeCount: number }): ReactElement {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin', 'system', 'overrides'], queryFn: fetchTraceOverrides });
  const [userId, setUserId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [ttl, setTtl] = useState('30');
  const mut = useMutation({
    mutationFn: () =>
      postTraceOverride({
        userId: userId || undefined,
        sessionId: sessionId || undefined,
        ttlMinutes: Number.parseInt(ttl, 10) || 30,
      }),
    onSuccess: () => {
      setUserId('');
      setSessionId('');
      void qc.invalidateQueries({ queryKey: ['admin', 'system'] });
    },
  });
  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">Trace overrides · {formatNumber(activeCount)} activos</h3>
      </header>
      <div className="form-grid">
        <label>userId <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="uuid" /></label>
        <label>sessionId <input value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="uuid" /></label>
        <label>TTL min <input value={ttl} onChange={(e) => setTtl(e.target.value)} /></label>
        <button
          type="button"
          className="btn-primary"
          disabled={mut.isPending || (!userId && !sessionId)}
          onClick={() => mut.mutate()}
        >
          {mut.isPending ? 'Creando…' : 'Boost sampling'}
        </button>
        {mut.isError && <p className="error">{(mut.error).message}</p>}
      </div>
      {list.isPending ? null : list.isError ? (
        <p className="error">Error al listar overrides</p>
      ) : (
        <table className="data-table" style={{ marginTop: 12 }}>
          <thead>
            <tr><th>User</th><th>Session</th><th>Expires</th><th>Created by</th></tr>
          </thead>
          <tbody>
            {list.data.items.length === 0 ? (
              <tr><td colSpan={4} className="empty">Sin overrides activos</td></tr>
            ) : (
              list.data.items.map((o) => (
                <tr key={o.id}>
                  <td>{o.userId ?? '—'}</td>
                  <td>{o.sessionId ?? '—'}</td>
                  <td>{formatDateTime(o.expiresAt)}</td>
                  <td>{o.createdBy}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}
